<template>
  <v-row no-gutters class="justify-center algin-center">
    <v-col cols="auto">
      <table-button key="toggleEditing" v-bind="mainButton" @toggleEditing="toggleEditing"> </table-button>
    </v-col>
    <v-col cols="auto" v-if="isEditingOn">
      <table-buttons
        :buttons="filteredButtons"
        @addNewObject="toggleAddNewObject"
        @changeGeometry="toggleEditObject"
        @copyObject="toggleCopyObject"
        @deleteObject="deleteObjectInit"
        @bulkEdit="openFieldCalculator"
        @invertLine="invertLineInit"
        @splitLine="toggleSplitingLine"
        @assignParcel="toggleAssignParcel"
      >
        <template #joinLines-outter="{ icon, fontSize, disabled, tooltip }">
          <table-buttons-edit-join-lines
            :selected-feature-id="selectedFeatureId"
            @joinLines="joinLineInit"
            :tooltip="tooltip"
            :icon="icon"
            :datasource="datasource"
            :font-size="fontSize"
            :disabled="disabled"
            :layer-id="layerId"
            :topological="isTopologicalLayer"
          ></table-buttons-edit-join-lines>
        </template>
      </table-buttons>
    </v-col>
    <main-dialog
      :is-visible.sync="isDialogVisible"
      v-if="isDialogLoaded"
      :loading="agreeButtonLoading"
      v-bind="dialogConfig"
      @agree="dialogConfig.agreeHandler"
      @modalClosed="onModalClosed"
    ></main-dialog>
  </v-row>
</template>

<script>
import { get, sync, call } from 'vuex-pathify';

import { prepareDrawnGeometry, createVectorSource } from '@/assets/js/mapUtils';
import TableButton from '@/components/TableButton';
import TableButtons from '@/components/TableButtons';
import TableButtonsEditJoinLines from '@/components/TableButtonsEditJoinLines';

import deleteObject from '@/mixins/deleteObject';

export default {
  name: 'TableButtonsEdit',
  mixins: [deleteObject],
  components: {
    MainDialog: () => import('@/components/MainDialog'),
    TableButton,
    TableButtons,
    TableButtonsEditJoinLines,
  },
  props: {
    selectedFeatures: {
      type: Array,
      default: () => [],
    },
    layerId: {
      type: [Number, String],
      required: true,
    },
    isAnyFeatureSelected: {
      type: Boolean,
      default: false,
    },
    datasource: {
      type: String,
      required: true,
    },
    geometryType: {
      validator: value => {
        return typeof value === 'string' || value === null;
      },
      required: true,
    },
    layerScope: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    isEditingOn: false,
    isDialogVisible: false,
    isDialogLoaded: false,
    isCopyButtonLoading: false,
    isSplittingLineButtonLoading: false,
    dialogType: 'delete',
    joinWithId: undefined,
    agreeButtonLoading: false,
    treeViewMainLayerId: null,
  }),
  computed: {
    currentLayer: get('layers/currentLayer'),
    currentLayerId: get('layers/currentLayer@id'),
    tableSelectionType: get('edit/tableSelectionType'),
    currentTab: sync('sidebar/currentTab'),
    newFeatureGeometry: sync('edit/newFeatureGeometry'),
    isAddingButtonActive: sync('edit/isAddingButtonActive'),
    isEditingButtonActive: sync('edit/isEditingButtonActive'),
    isCopyButtonActive: sync('edit/isCopyButtonActive'),
    isSplittingLineButtonActive: sync('edit/isSplittingLineButtonActive'),
    isAssignParcelButtonActive: sync('edit/isAssignParcelButtonActive'),
    copiedObject: sync('edit/copiedObject'),
    activeTool: get('tools/activeTool'),
    treeViewMetadata: get('attributesSchema/treeViewMetadata@:currentLayerId'),
    isActiveToolNotReplaceable: get('tools/isActiveToolNotReplaceable'),
    zdmStreetLampDataSourceName: get('admin/modulesMapping@zdm_data.datasources.street_lamp.datasource'),
    zdmStreetLampFields: get('admin/modulesMapping@zdm_data.street_lamp_fields'),
    attachmentsDataSourceName: get('admin/modulesMapping@documentation.attachment_datasource_name'),
    geometryPropertyName: get('layers/metadata@:datasource.attributes_schema.geometry_name'),
    currentLayerAttributesMetadata: get('layers/metadata@:datasource.attributes_schema.attributes'),
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    geometryReadOnly() {
      return (
        this.currentLayerAttributesMetadata?.find(attribute => attribute.name === this.geometryPropertyName)
          ?.read_only || false
      );
    },
    fid() {
      return this.$route.params.fid;
    },
    selectedFeatureId() {
      const {
        selectedFeatures: [id],
      } = this;
      return id;
    },
    selectedFeaturesLength() {
      return this.selectedFeatures.length;
    },
    isOneFeatureSelected() {
      return this.selectedFeaturesLength === 1;
    },
    isAtLeastOneObjectSelected() {
      return this.selectedFeaturesLength >= 1;
    },
    isMoreThanOneObjectSelected() {
      return this.selectedFeaturesLength > 1;
    },
    mainButton() {
      return {
        name: 'toggleEditing',
        icon: 'mdi-pencil',
        fontSize: '19px',
        isActive: this.isEditingOn,
        hasState: true,
        tooltip: this.isEditingOn
          ? this.$i18n.t('button.toggleEditing', { onOff: this.$i18n.t('button.off') })
          : this.$i18n.t('button.toggleEditing', { onOff: this.$i18n.t('button.on') }),
      };
    },
    isGeometricalLayer() {
      return Boolean(this.currentLayer.geometry_type);
    },
    isTopologicalLayer() {
      return this.layerScope === 'water' || this.layerScope === 'sewer';
    },
    isLineStringGeometry() {
      return this.geometryType === 'linestring' || this.geometryType === 'multilinestring';
    },
    isPolygonGeometry() {
      return this.geometryType === 'polygon' || this.geometryType === 'multipolygon';
    },
    streetLampFieldsDataSources() {
      let dataSources = [];
      if (this.zdmStreetLampFields) {
        dataSources = Object.keys(this.zdmStreetLampFields);
      }
      if (this.zdmStreetLampDataSourceName) {
        dataSources.push(this.zdmStreetLampDataSourceName);
      }
      return dataSources;
    },
    isAttachmentsDatasource() {
      return this.datasource === this.attachmentsDataSourceName;
    },
    buttons() {
      return [
        ...(!this.isAttachmentsDatasource && !this.geometryReadOnly
          ? [
              {
                name: 'addNewObject',
                icon: 'mdi-plus-circle',
                disabled:
                  this.isEditingButtonActive ||
                  this.isCopyButtonActive ||
                  this.isSplittingLineButtonActive ||
                  this.isAssignParcelButtonActive ||
                  this.isActiveToolNotReplaceable,
                tooltip: this.getTooltip(
                  'button.addNewObject',
                  'button.toChangeGeometry',
                  'button.justOneObject',
                  true,
                  this.isEditingButtonActive ||
                    this.isCopyButtonActive ||
                    this.isSplittingLineButtonActive ||
                    this.isAssignParcelButtonActive ||
                    this.isActiveToolNotReplaceable
                ),
                fontSize: '19px',
                hasState: true,
                isActive: this.isAddingButtonActive,
              },
            ]
          : []),
        ...(this.isGeometricalLayer && !this.geometryReadOnly
          ? [
              {
                name: 'changeGeometry',
                icon: 'mdi-map-marker',
                disabled:
                  !this.isOneFeatureSelected ||
                  this.isAddingButtonActive ||
                  this.isCopyButtonActive ||
                  this.isAssignParcelButtonActive ||
                  this.isActiveToolNotReplaceable,
                tooltip: this.getTooltip(
                  'button.changeGeometry',
                  'button.toChangeGeometry',
                  'button.justOneObject',
                  this.isOneFeatureSelected,
                  this.isAddingButtonActive ||
                    this.isCopyButtonActive ||
                    this.isSplittingLineButtonActive ||
                    this.isAssignParcelButtonActive ||
                    this.isActiveToolNotReplaceable
                ),
                fontSize: '19px',
                hasState: true,
                isActive: this.isEditingButtonActive,
              },
            ]
          : []),
        {
          name: 'bulkEdit',
          icon: 'mdi-table-large',
          disabled: !this.isMoreThanOneObjectSelected,
          tooltip: this.getTooltip(
            'button.bulkAttributesEdit',
            'button.toBulkEdit',
            'button.moreThanOneObject',
            this.isMoreThanOneObjectSelected
          ),
          fontSize: '19px',
        },
        ...(!this.isAttachmentsDatasource && !this.geometryReadOnly
          ? [
              {
                name: 'copyObject',
                icon: 'mdi-content-copy',
                disabled:
                  !this.isOneFeatureSelected ||
                  this.isAddingButtonActive ||
                  this.isEditingButtonActive ||
                  this.isSplittingLineButtonActive ||
                  this.isAssignParcelButtonActive ||
                  this.isActiveToolNotReplaceable,
                tooltip: this.getTooltip(
                  'button.copyObject',
                  'button.toCopyObject',
                  'button.justOneObject',
                  this.isOneFeatureSelected,
                  this.isAddingButtonActive ||
                    this.isEditingButtonActive ||
                    this.isSplittingLineButtonActive ||
                    this.isAssignParcelButtonActive ||
                    this.isActiveToolNotReplaceable
                ),
                fontSize: '19px',
                hasState: true,
                isActive: this.isCopyButtonActive,
                isLoading: this.isCopyButtonLoading,
              },
            ]
          : []),
        ...(this.isGeometricalLayer && !this.geometryReadOnly
          ? [
              {
                name: 'invertLine',
                icon: 'mdi-swap-horizontal',
                disabled:
                  !this.isOneFeatureSelected ||
                  this.isAddingButtonActive ||
                  this.isEditingButtonActive ||
                  this.isSplittingLineButtonActive ||
                  this.isAssignParcelButtonActive,
                tooltip: this.getTooltip(
                  'button.invertLine',
                  'button.toInvertLine',
                  'button.justOneObject',
                  this.isOneFeatureSelected,
                  this.isAddingButtonActive ||
                    this.isEditingButtonActive ||
                    this.isSplittingLineButtonActive ||
                    this.isAssignParcelButtonActive
                ),
                fontSize: '19px',
                isNotVisible: !(this.isTopologicalLayer && this.isLineStringGeometry),
              },
              {
                name: 'splitLine',
                icon: 'mdi-box-cutter',
                disabled:
                  !this.isOneFeatureSelected ||
                  this.isAddingButtonActive ||
                  this.isEditingButtonActive ||
                  this.isAssignParcelButtonActive ||
                  this.isActiveToolNotReplaceable,
                tooltip: this.getTooltip(
                  'button.splitLine',
                  'button.toSplitLine',
                  'button.justOneObject',
                  this.isOneFeatureSelected,
                  this.isAddingButtonActive ||
                    this.isEditingButtonActive ||
                    this.isAssignParcelButtonActive ||
                    this.isActiveToolNotReplaceable
                ),
                fontSize: '19px',
                hasState: true,
                isActive: this.isSplittingLineButtonActive,
                isLoading: this.isSplittingLineButtonLoading,
                isNotVisible: !this.isLineStringGeometry,
              },
              {
                name: 'joinLines',
                icon: 'mdi-vector-polyline',
                disabled:
                  !this.isOneFeatureSelected ||
                  this.isAddingButtonActive ||
                  this.isEditingButtonActive ||
                  this.isSplittingLineButtonActive ||
                  this.isAssignParcelButtonActive,
                tooltip: this.getTooltip(
                  'button.joinLines',
                  'button.toJoinLines',
                  'button.justOneObject',
                  this.isOneFeatureSelected,
                  this.isAddingButtonActive ||
                    this.isEditingButtonActive ||
                    this.isSplittingLineButtonActive ||
                    this.isAssignParcelButtonActive
                ),
                fontSize: '19px',
                isNotVisible: !this.isLineStringGeometry,
              },
              {
                name: 'assignParcel',
                icon: 'mdi-crosshairs-gps',
                disabled:
                  this.isAddingButtonActive ||
                  this.isEditingButtonActive ||
                  this.isCopyButtonActive ||
                  this.isSplittingLineButtonActive ||
                  (this.activeTool !== 'moduleIdentification' &&
                    this.activeTool !== 'assignPlot' &&
                    this.isActiveToolNotReplaceable),
                tooltip: this.getTooltip(
                  'button.assignParcelGeometry',
                  'button.toChangeGeometry',
                  'button.justOneObject',
                  true,
                  this.isAddingButtonActive ||
                    this.isEditingButtonActive ||
                    this.isCopyButtonActive ||
                    this.isSplittingLineButtonActive ||
                    (this.activeTool !== 'moduleIdentification' &&
                      this.activeTool !== 'assignPlot' &&
                      this.isActiveToolNotReplaceable)
                ),
                fontSize: '19px',
                hasState: true,
                isActive: this.isAssignParcelButtonActive,
                isNotVisible: !this.isPolygonGeometry,
              },
            ]
          : []),
        {
          name: 'deleteObject',
          icon: 'mdi-delete',
          disabled: !this.isAtLeastOneObjectSelected,
          tooltip: this.getTooltip(
            this.isOneFeatureSelected ? 'button.deleteObject' : 'button.deleteObjects',
            'button.noObjectsSelected',
            'button.toDeleteObject',
            this.isAtLeastOneObjectSelected
          ),
          fontSize: '19px',
        },
      ];
    },
    filteredButtons() {
      return this.buttons.filter(button => !button.isNotVisible);
    },
    dialogConfig() {
      const dict = {
        delete: {
          title: this.isOneFeatureSelected
            ? this.$i18n.t('dialog.title.deleteObject')
            : this.$i18n.t('dialog.title.deleteObjects'),
          body: this.isOneFeatureSelected
            ? this.$i18n.t('dialog.body.deleteObject', { id: this.selectedFeatureId })
            : this.$i18n.t('dialog.body.deleteObjects'),
          agreeText: 'dialog.agreeDelete',
          agreeHandler: this.deleteObjectHandler,
        },
        invert: {
          title: this.$i18n.t('dialog.title.invertLine'),
          body: this.$i18n.t('dialog.body.invertLine', { id: this.selectedFeatureId }),
          agreeText: 'dialog.invert',
          agreeHandler: this.invertLineHandler,
        },
        split: {
          title: this.$i18n.t('dialog.title.splitLine'),
          body: this.$i18n.t('dialog.body.splitLine', { id: this.selectedFeatureId }),
          agreeText: 'dialog.split',
          agreeHandler: this.splitLineHandler,
        },
        join: {
          title: this.$i18n.t('dialog.title.joinLine'),
          body: this.$i18n.t('dialog.body.joinLine', { id: this.selectedFeatureId, joinWithId: this.joinWithId }),
          agreeText: 'dialog.join',
          agreeHandler: this.joinLinesHandler,
        },
      };
      return dict[this.dialogType];
    },
  },
  methods: {
    getTreeMetadata: call('attributesSchema/getTreeMetadata'),
    getLayerFeature: call('layers/getLayerFeature'),
    invertLine: call('layers/invertLine'),
    joinTopologicalLines: call('layers/joinTopologicalLines'),
    joinLines: call('layers/joinLines'),
    splitTopologicalLine: call('layers/splitTopologicalLine'),
    splitLine: call('layers/splitLine'),
    // TODO : fix getTooltip to ignore requiretment and suffix for assignParcel and addGeometry
    getTooltip(activeTooltip, requirement, suffix, isRequirementsFulfilled, isAnyToolActive) {
      if (isAnyToolActive) {
        return this.$i18n.t('button.oneToolActive');
      }
      return isRequirementsFulfilled
        ? this.$i18n.t(activeTooltip)
        : this.$i18n.t(requirement, { suffix: this.$i18n.t(suffix) });
    },
    tableLayerChanged(oV, nV) {
      if ((nV?.id || nV?.id === 0) && nV?.id == this.treeViewMainLayerId) {
        return;
      }
      this.turnOffEditing();
    },
    toggleEditing() {
      if (this.isEditingOn) {
        this.turnOffEditing();
        return;
      }
      this.isEditingOn = !this.isEditingOn;
    },
    turnOffEditing() {
      if (this.isAddingButtonActive) {
        this.turnOffAddNewObject();
      }
      if (this.isEditingButtonActive) {
        this.turnOffEditObject();
      }
      if (this.isCopyButtonActive) {
        this.turnOffCopying();
      }
      this.isEditingOn = false;
    },
    pushToParentRoute() {
      if (!this.$route.meta.parent === this.$route.name) {
        return;
      }
      this.$router.push({
        name: this.$route.meta.parent,
        params: {
          lid: this.layerId,
        },
      });
      this.currentTab = 0;
    },
    setSidebarGeometry(feature) {
      if (!feature) {
        this.newFeatureGeometry = null;
      } else {
        this.newFeatureGeometry = prepareDrawnGeometry(feature);
      }
    },
    onModifyend(e) {
      this.setSidebarGeometry(e.features.getArray()[0]);
    },
    onDrawend(e) {
      this.setSidebarGeometry(e.feature);
      if (this.streetLampFieldsDataSources.includes(this.datasource)) {
        if (this.$route.name === 'addingLamp') {
          return;
        }
        this.$router.push({
          name: 'addingLamp',
          params: {
            ...this.$route.params,
          },
        });
        return;
      }
      const layerId =
        this.treeViewMainLayerId || this.treeViewMainLayerId === 0 ? this.treeViewMainLayerId : this.layerId;
      this.$router.push({
        name: 'layerFeature',
        params: {
          ...this.$route.params,
          lid: layerId,
          fid: 'new',
        },
      });
    },
    openDialog(type) {
      this.dialogType = type;
      this.isDialogLoaded = true;
      this.isDialogVisible = true;
    },
    invertLineInit() {
      this.openDialog('invert');
    },
    joinLineInit(id) {
      this.joinWithId = id;
      this.openDialog('join');
    },
    deleteObjectInit() {
      this.openDialog('delete');
    },
    onModalClosed() {
      if (this.dialogType === 'split') {
        this.turnOffSplittingLine();
      }
    },
    clearModal() {
      this.agreeButtonLoading = false;
      this.isDialogVisible = false;
    },
    async agreeHof(fn, payload, message, successFunction) {
      try {
        this.agreeButtonLoading = true;
        const r = await fn(payload);
        this.pushMessage(message);
        this.clearModal();
        if (successFunction) {
          successFunction(payload);
        } else {
          this.refreshLayersAndTable(r.data.data);
        }
        return r;
      } catch (error) {
        this.clearModal();
        throw new Error(error);
      }
    },
    async joinLinesHandler() {
      const { selectedFeatureId: id, joinWithId, datasource } = this;
      await this.agreeHof(
        this.isTopologicalLayer ? this.joinTopologicalLines : this.joinLines,
        this.isTopologicalLayer
          ? { body: { features: [id, joinWithId] }, datasource }
          : { body: { to_merge: [joinWithId] }, datasource, id },
        this.$i18n.t('success.joinedLines'),
        !this.isTopologicalLayer ? this.refreshLayersAndTableByDataSource : null
      );
    },
    async invertLineHandler() {
      const { selectedFeatureId: id, datasource } = this;
      await this.agreeHof(this.invertLine, { id, datasource }, this.$i18n.t('success.invertedLine'));
    },
    async splitLineHandler() {
      const { selectedFeatureId: id, datasource, splitGeometry } = this;
      await this.agreeHof(
        this.isTopologicalLayer ? this.splitTopologicalLine : this.splitLine,
        { id, datasource, body: { split_point: splitGeometry } },
        this.$i18n.t('success.splitedLine'),
        !this.isTopologicalLayer ? this.refreshLayersAndTableByDataSource : null
      );
      this.turnOffSplittingLine();
    },
    pushMessage(message) {
      this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
        message,
      });
    },
    onSplitLineDrawEnd(e) {
      this.splitGeometry = prepareDrawnGeometry(e.feature);
      this.openDialog('split');
    },
    toggleSplitingLine() {
      if (this.isSplittingLineButtonActive) {
        this.turnOffSplittingLine();
      } else {
        this.turnOnSplittingLine();
      }
    },
    turnOffSplittingLine() {
      this.$root.$emit('deleteSidebarGeometry');
      this.$root.$emit('turnOffEditing', false);
      this.$root.$emit('toggleSnappingInteraction', { value: false });
      this.$root.$emit('selection-action', this.tableSelectionType);
      this.isSplittingLineButtonActive = false;
    },
    async turnOnSplittingLine() {
      this.$root.$emit('deactivateAllTools');
      await this.$nextTick(async () => {
        this.isSplittingLineButtonLoading = true;
        this.$root.$emit('selection-action');
        const { layerId: layer_id, selectedFeatureId } = this;
        const r = await this.getLayerFeature({ layer_id, feature_id: selectedFeatureId });
        const snappingVectorSource = createVectorSource([r]);
        this.turnOnSplittngDrawing(snappingVectorSource);
        this.turnOnSplittngSnapping(snappingVectorSource);
        this.isSplittingLineButtonLoading = false;
        this.isSplittingLineButtonActive = true;
      });
    },
    turnOnSplittngDrawing(source) {
      this.$root.$emit('drawSidebarGeometry', {
        geometryType: 'Point',
        drawendCallback: this.onSplitLineDrawEnd,
        condition: e => {
          const intersects = source
            .getFeatures()[0]
            .getGeometry()
            .intersectsCoordinate(e.coordinate);
          if (!intersects) {
            this.$store.set('snackbar/PUSH_ERROR!', {
              message: this.$i18n.t('snackbar.pointNotIntersects'),
            });
            return;
          }
          return true;
        },
      });
    },
    turnOnSplittngSnapping(source) {
      this.$root.$emit('toggleSnappingInteraction', {
        value: true,
        source,
      });
    },
    async deleteObjectHof(fn) {
      try {
        const { datasource, selectedFeatureId, selectedFeatures } = this;
        this.agreeButtonLoading = true;
        const r =
          this.isMoreThanOneObjectSelected || this.isTopologicalLayer
            ? await fn({ datasource, payload: { to_delete: selectedFeatures } })
            : await fn({ datasource, id: selectedFeatureId });
        this.$emit('refreshTableData');
        this.$root.$emit('refreshMvtSource', this.layerId);
        if (selectedFeatureId == this.fid) {
          this.pushToParentRoute();
        }
        this.isDialogVisible = false;
        this.agreeButtonLoading = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.isOneFeatureSelected
            ? this.$i18n.t('success.successDeleteObject')
            : this.$i18n.t('success.successDeleteObjects'),
        });
        return r;
      } catch (error) {
        console.log(error);
        this.isDialogVisible = false;
      }
    },
    toggleAddNewObject() {
      if (this.isAddingButtonActive) {
        this.turnOffAddNewObject();
      } else {
        this.turnOnAddNewObject();
      }
    },
    turnOnAddNewObject() {
      this.$root.$emit('deactivateAllTools');
      this.$nextTick(() => {
        this.isAddingButtonActive = true;
        if (this.isGeometricalLayer) {
          this.$root.$emit('drawSidebarGeometry', {
            geometryType: this.geometryType,
            drawendCallback: this.onDrawend,
            layerId: this.layerId,
          });
        } else {
          this.onNewPolygonFeatureGeometry({ inputName: 'newPolygonFeatureGeometry', geometry: undefined });
        }
      });
    },
    turnOffAddNewObject() {
      this.isAddingButtonActive = false;
      this.$root.$emit('deleteSidebarGeometry');
      this.pushToParentRoute();
      this.$root.$emit('turnOffEditing', false);
    },
    toggleAssignParcel() {
      if (this.isAssignParcelButtonActive) {
        this.turnOffAssignParcel();
      } else {
        this.turnOnAssignParcel();
      }
    },
    turnOnAssignParcel() {
      this.$root.$emit('deactivateAllTools');
      this.$nextTick(() => {
        this.isAssignParcelButtonActive = true;
        this.$root.$emit('identification-action', true, {
          specialType: 'assignPlot',
          plotName: 'newPolygonFeatureGeometry',
          isTurnOffOnFail: false,
        });
      });
    },
    turnOffAssignParcel() {
      this.isAssignParcelButtonActive = false;
      this.$root.$emit('deleteSidebarGeometry');
      this.pushToParentRoute();
      this.$root.$emit('identification-action', false);
      this.$root.$emit('turnOffEditing', false);
    },
    onNewPolygonFeatureGeometry({ inputName, geometry }) {
      if (inputName === 'newPolygonFeatureGeometry') {
        this.newFeatureGeometry = geometry;
        this.$router.push({
          name: 'layerFeature',
          params: {
            ...this.$route.params,
            lid: this.layerId,
            fid: 'new',
          },
        });
      }
    },
    isFeatureCardOpened(id) {
      return this.fid == id;
    },
    async toggleEditObject() {
      if (this.isEditingButtonActive) {
        this.turnOffEditObject();
      } else {
        this.turnOnEditObject();
      }
    },
    async turnOnEditObject() {
      this.$root.$emit('deactivateAllTools');
      await this.$nextTick(async () => {
        const { selectedFeatureId } = this;
        if (!this.isFeatureCardOpened(selectedFeatureId)) {
          await this.$router.push({
            name: 'layerFeature',
            params: {
              ...this.$route.params,
              lid: this.layerId,
              fid: selectedFeatureId,
            },
          });
        }
        this.isEditingButtonActive = true;
      });
    },
    turnOffEditObject() {
      this.isEditingButtonActive = false;
      this.$root.$emit('turnOffEditing', false);
    },
    toggleCopyObject() {
      if (this.isCopyButtonActive) {
        this.turnOffCopying();
      } else {
        this.turnOnCopying();
      }
    },
    async turnOnCopying() {
      this.$root.$emit('deactivateAllTools');
      await this.$nextTick(async () => {
        try {
          this.isCopyButtonActive = true;
          this.isCopyButtonLoading = true;
          const { layerId: layer_id, selectedFeatureId } = this;
          this.$root.$emit('selection-action');
          if (this.isGeometricalLayer) {
            this.$root.$emit('drawSidebarGeometry', {
              geometryType: this.geometryType,
              drawendCallback: this.onDrawend,
            });
          }
          if (this.datasource !== this.zdmStreetLampDataSourceName) {
            const r = await this.getLayerFeature({ layer_id, feature_id: selectedFeatureId });
            this.copiedObject = r.properties;
          } else {
            this.copiedObject = selectedFeatureId;
          }
          if (!this.isGeometricalLayer) {
            this.onNewPolygonFeatureGeometry({ inputName: 'newPolygonFeatureGeometry', geometry: undefined });
          }
        } catch (error) {
          console.log('~ error', error);
        }
        this.isCopyButtonLoading = false;
      });
    },
    turnOffCopying() {
      this.isCopyButtonActive = false;
      this.$root.$emit('deleteSidebarGeometry');
      this.pushToParentRoute();
      this.$root.$emit('turnOffEditing', false);
    },
    openFieldCalculator() {
      this.$router.push({ name: 'fieldCalculator', params: { lid: this.layerId } });
    },
    async getTreeLayerMetadata(lId) {
      if (!lId && lId !== 0) {
        return;
      }
      await this.getTreeMetadata({ layerId: lId });
      const cLayerMetadata = this.treeViewMetadata?.find(metadata => metadata.layer === lId);
      this.treeViewMainLayerId = cLayerMetadata.path[0];
    },
    refreshLayersAndTableByDataSource(payload) {
      const { datasource } = payload;
      const layers = this.projectLayers.filter(layer => layer.data_source_name === datasource);
      const layersIds = layers.map(layer => layer.id);
      this.refreshLayersAndTable(layersIds);
    },
  },
  watch: {
    activeTool(nV, oV) {
      if (!oV && nV) {
        if (this.isAddingButtonActive) {
          this.turnOffAddNewObject();
        }
        if (this.isEditingButtonActive) {
          this.turnOffEditObject();
        }
        if (this.isCopyButtonActive) {
          this.turnOffCopying();
        }
        if (this.isSplittingLineButtonActive) {
          this.turnOffSplittingLine();
        }
        if (this.isAssignParcelButtonActive && nV !== 'assignPlot') {
          this.turnOffAssignParcel();
        }
      }
    },
    currentLayerId: {
      immediate: true,
      handler(nV, oV) {
        if (!this.currentLayer.tree_view) {
          return;
        }
        if ((oV || oV === 0) && (nV || nV === 0) && nV === oV) {
          this.treeViewMainLayerId = null;
          return;
        }
        this.getTreeLayerMetadata(nV);
      },
    },
  },
  mounted() {
    this.$root.$on('turnOnCopying', this.turnOnCopying);
    this.$root.$on('addedSidebarGeometry', this.onNewPolygonFeatureGeometry);
    this.$once('hook:beforeDestroy', () => {
      this.$root.$off('turnOnCopying');
    });
  },
};
</script>

<style lang="scss" scoped></style>
